import {Fragment, type FC, useCallback, useEffect, useId, useRef, useState} from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  Box,
  SimpleGrid,
  Text,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import {PolarEmbedCheckout} from "@polar-sh/checkout/embed";
import type {
  ICreditAllowedActions,
  ICreditCancelEffective,
  ICreditErrorBody,
  ICreditErrorReason,
  ICreditMonthlyPlanId,
  ICreditNextAction,
  ICreditPackage,
  ICreditPurchase,
  ICreditRefund,
  ICreditRefundQuote,
  ICreditRefundResponse,
  ICreditSubscription,
  ICreditSubscriptionChangeResponse,
  ICreditUpgradeQuote,
  ICreditUsageRow,
  ICreditsBalance,
} from "../../models/interfaces/credits.interface.ts";
import {getCreditPurchases, getCreditUsage} from "../../services/creditsService.ts";
import {isAmbiguousResult, type PricingView, useCreditsStore} from "../../store/useCreditsStore.ts";
import useUserStore from "../../store/useUserStore.ts";
import SmartIcon from "../SmartIcon.tsx";
import BillingActionConfirmation, {
  type BillingActionPhase,
  type BillingSummaryRow,
  POLAR_CHECKOUT_NOTE,
  POLAR_REFUND_NOTE,
  SIMULATED_NOTE,
} from "./BillingActionConfirmation.tsx";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog.tsx";
import PricingPackageCard, {
  CREDITS_CONTACT_EMAIL,
  type PricingCardAction,
  type PricingCtaState,
} from "./PricingPackageCard.tsx";
import PurchaseHistory from "./PurchaseHistory.tsx";
import SubscriptionPanel, {MemberNotice, PlanSummaryLine} from "./SubscriptionPanel.tsx";
import UsageHistory from "./UsageHistory.tsx";

// Headless UI Dialog, not a Chakra Modal: it must stack above the Create dialog (also a headless
// Dialog), which makes any other portal inert and closes on clicks inside it.

const PURCHASES_DISABLED_CODE = 1011;
const PURCHASES_DISABLED_MESSAGE = `Purchases are not available yet - contact ${CREDITS_CONTACT_EMAIL}`;
const PURCHASE_FAILED_MESSAGE = "Purchase failed";
const PURCHASE_PROCESSING_MESSAGE = "Purchase is still processing - credits are added once the payment is confirmed";
const PURCHASE_UNCONFIRMED_MESSAGE = "We couldn't confirm the purchase. Retrying is safe - you won't be charged twice.";
const QUOTE_UNAVAILABLE_MESSAGE = "The quote could not be prepared. Close this dialog and try again.";
const GENERIC_FAILURE_MESSAGE = "The request failed. Nothing was changed.";
const RESULT_PROCESSING_MESSAGE = "The request is still processing. Your current plan and balance remain authoritative.";
const RESULT_FAILED_MESSAGE = "The request finished without completing the requested change.";
const UNKNOWN_RESULT_MESSAGE =
  "We couldn't confirm the result. Confirm again to retry safely: the same request is replayed, never duplicated.";
const REASON_MESSAGES: Record<ICreditErrorReason, string> = {
  stale_version:
    "This subscription changed since you opened this dialog. The details were refreshed - review them and confirm again.",
  change_in_progress: "Another billing change is still in progress. Try again in a moment.",
  usage_settlement_pending: "Usage on this plan is still settling. Try again once the running projects finish.",
  refund_not_allowed: "These credits can't be refunded.",
  idempotency_mismatch: "This request no longer matches its first attempt. Close this dialog and start again.",
  invalid_plan_transition: "That plan isn't a valid change from your current plan. Pick another plan.",
  client_outdated: "This page is out of date. Reload it to manage your plan.",
};
const PLAN_NAMES: Record<string, string> = {pro: "Pro", business: "Business", scale: "Scale"};
const CHECKOUT_OPEN_FAILED_MESSAGE = "Couldn't open payment window";
const CHECKOUT_PROCESSING_MESSAGE = "Payment received - adding your credits";
const CHECKOUT_TIMEOUT_MESSAGE = "Payment received, credits will appear shortly";
const CHECKOUT_FAILED_MESSAGE = "The payment did not complete. No credits were added.";

const PRICING_VIEWS = ["plans", "billing"] as const satisfies readonly PricingView[];
// ::before stretches each 36px segment over the track padding to a 44px target without overlapping its neighbour.
const VIEW_TAB_CLASS =
  "relative z-10 flex h-9 min-w-24 items-center justify-center rounded-full px-4 text-sm font-semibold "
  + "transition-[color,transform] duration-200 active:scale-[0.96] motion-reduce:transition-colors "
  + "before:absolute before:inset-x-0 before:-inset-y-1 aria-selected:text-white "
  + "aria-[selected=false]:text-n-500 aria-[selected=false]:hover:text-n-700 data-[focus]:outline "
  + "data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-brandPurple-400";
const VIEW_PANEL_CLASS =
  "rounded-2xl data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-4 "
  + "data-[focus]:outline-brandPurple-400";

// "embed": the Polar checkout iframe is open and the pricing dialog is hidden, so its focus trap cannot keep
// focus away from the iframe. Every other phase reopens the pricing dialog with the result on top.
type CheckoutPhase = "embed" | "processing" | "done" | "timeout" | "failed";

interface CheckoutRequest {
  action: "purchase" | "upgrade";
  packageId: string;
  purchaseId: string;
  url: string;
}

interface CheckoutFlow {
  action: "purchase" | "upgrade";
  packageId: string;
  purchaseId: string;
  phase: CheckoutPhase;
}

// Pre-core balances carry no allowedActions; the server enforces every mutation regardless.
const LEGACY_ACTIONS: ICreditAllowedActions = {
  buyStarted: true,
  subscribe: true,
  upgrade: false,
  downgrade: false,
  cancel: false,
  resume: false,
  refund: false,
};

const utcFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
const usdFormatter = new Intl.NumberFormat("en-US", {style: "currency", currency: "USD"});

const formatUtc = (iso: string): string => `${utcFormatter.format(new Date(iso))}\u00a0UTC`;
const formatUsdCents = (cents: number): string => usdFormatter.format(cents / 100);
const formatCreditCount = (credits: number): string => `${credits.toLocaleString("en-US")} credits`;
// Only an amount the server marks simulated says so; a Polar-mode quote or refund is a real payment.
const amountCents = (cents: number, simulated: boolean): string =>
  simulated ? `${formatUsdCents(cents)} (simulated)` : formatUsdCents(cents);

const isPurchasesDisabledError = (error: unknown): boolean =>
  axios.isAxiosError<{code?: number}>(error) &&
  error.response?.status === 409 &&
  error.response.data?.code === PURCHASES_DISABLED_CODE;

const errorReason = (error: unknown): ICreditErrorReason | null =>
  axios.isAxiosError<ICreditErrorBody>(error) ? error.response?.data?.reason ?? null : null;

const isStaleVersionError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 409 && errorReason(error) === "stale_version";

// A lost response, a timeout or a 5xx may follow a commit; only a definite rejection may say nothing changed.
const billingErrorMessage = (error: unknown): string => {
  if (isAmbiguousResult(error)) return UNKNOWN_RESULT_MESSAGE;
  if (!axios.isAxiosError<ICreditErrorBody>(error) || error.response === undefined) return GENERIC_FAILURE_MESSAGE;
  const reason = errorReason(error);
  if (reason !== null && reason in REASON_MESSAGES) return REASON_MESSAGES[reason];
  if (isPurchasesDisabledError(error)) return PURCHASES_DISABLED_MESSAGE;
  if (error.response.status === 403) return "Only workspace admins can change billing.";
  return GENERIC_FAILURE_MESSAGE;
};

type BillingActionKind = "upgrade" | "downgrade" | "resume" | "cancel" | "refund";
type BillingActionResult = ICreditSubscriptionChangeResponse | ICreditRefundResponse;

interface ActiveAction {
  kind: BillingActionKind;
  phase: BillingActionPhase;
  message: string | null;
  subscription: ICreditSubscription | null;
  // The balance a cancellation preview is prepared from: a later read never rewrites the confirmation under review.
  balanceSnapshot: ICreditsBalance | null;
  targetPackage: ICreditPackage | null;
  upgradeQuote: ICreditUpgradeQuote | null;
  refundPurchase: ICreditPurchase | null;
  refundQuote: ICreditRefundQuote | null;
  result: BillingActionResult | null;
}

interface ActionTicket {
  kind: BillingActionKind;
  token: number;
  startedAt: string;
}

interface ConfirmationView {
  title: string;
  intro: string | null;
  rows: BillingSummaryRow[];
  note: string | null;
  confirmLabel: string;
  dismissLabel: string;
  isConfirmDisabled: boolean;
}

const startAction = (kind: BillingActionKind, patch: Partial<ActiveAction> = {}): ActiveAction => ({
  kind,
  phase: "ready",
  message: null,
  subscription: null,
  balanceSnapshot: null,
  targetPackage: null,
  upgradeQuote: null,
  refundPurchase: null,
  refundQuote: null,
  result: null,
  ...patch,
});

const liveSubscriptionOf = (balance: ICreditsBalance | null): ICreditSubscription | null =>
  balance?.subscription?.live ? balance.subscription : null;

const isMonthly = (pkg: ICreditPackage): boolean => pkg.interval === "month";

// Catalog ids are plain strings; only a known monthly plan can be the target of an upgrade or a downgrade.
const MONTHLY_PLAN_IDS: readonly ICreditMonthlyPlanId[] = ["pro", "business", "scale"];
const monthlyPlanIdOf = (pkg: ICreditPackage | null): ICreditMonthlyPlanId | null =>
  MONTHLY_PLAN_IDS.find((planId) => planId === pkg?.id) ?? null;

const isChangeResponse = (result: BillingActionResult): result is ICreditSubscriptionChangeResponse =>
  "subscription" in result;

const resultPhase = (
  kind: BillingActionKind,
  result: BillingActionResult,
  targetPlanId: ICreditMonthlyPlanId | null,
): BillingActionPhase => {
  if (result.httpStatus === 202) return "processing";
  if (kind === "upgrade" && isChangeResponse(result)) {
    if (result.subscription.changeInProgress) return "processing";
    return result.subscription.planId === targetPlanId ? "done" : "failed";
  }
  // The response is the subscription as it is now: a replay after another re-target can carry a different schedule.
  if (kind === "downgrade" && isChangeResponse(result)) {
    if (result.subscription.changeInProgress) return "processing";
    return result.subscription.nextPlanId === targetPlanId ? "done" : "failed";
  }
  if (kind === "refund" && !isChangeResponse(result)) {
    if (result.refund.status === "succeeded") return "done";
    if (result.refund.status === "failed") return "failed";
    return "processing";
  }
  if (isChangeResponse(result) && result.subscription.changeInProgress) return "processing";
  return "done";
};

const resultMessage = (phase: BillingActionPhase): string | null => {
  if (phase === "processing") return RESULT_PROCESSING_MESSAGE;
  if (phase === "failed") return RESULT_FAILED_MESSAGE;
  return null;
};

const requestFence = (): string => {
  const {workspaceId, generation} = useCreditsStore.getState();
  return `${workspaceId ?? ""}:${generation}`;
};

const refundLabel = (refund: ICreditRefund | null): string =>
  refund ? `${formatCreditCount(refund.credits)} - ${amountCents(refund.amountCents, refund.simulated)}` : "None";

// isSimulatedMode is the catalog's purchase mode, used only where no quote or purchase says how it is paid.
const describeAction = (
  action: ActiveAction,
  currentBalance: ICreditsBalance | null,
  planName: (planId: string) => string,
  isSimulatedMode: boolean,
): ConfirmationView => {
  const {subscription, result} = action;
  const change = result && isChangeResponse(result) ? result : null;
  const currentSubscription = currentBalance?.subscription ?? null;
  const done = action.phase === "done";
  const modeNote = isSimulatedMode ? SIMULATED_NOTE : null;
  switch (action.kind) {
    case "upgrade": {
      const currentName = subscription ? planName(subscription.planId) : "your plan";
      if (action.phase === "processing" || action.phase === "failed") {
        return {
          title: action.phase === "processing" ? "Upgrade processing" : "Upgrade failed",
          intro: null,
          rows: [],
          note: modeNote,
          confirmLabel: "Confirm upgrade",
          dismissLabel: "Not now",
          isConfirmDisabled: true,
        };
      }
      if (done) {
        return {
          title: "Upgrade complete",
          intro: currentSubscription
            ? `${planName(currentSubscription.planId)} is active from now; the unused ${currentName} credits were refunded separately.`
            : "Your balance was refreshed.",
          rows: currentBalance
            ? [
              {label: "Plan credits this period", value: formatCreditCount(currentBalance.planRemainingCredits)},
              {label: "Refund recorded", value: refundLabel(change?.refund ?? null)},
              {label: "Balance", value: formatCreditCount(currentBalance.balance)},
            ]
            : [],
          note: modeNote,
          confirmLabel: "Confirm upgrade",
          dismissLabel: "Not now",
          isConfirmDisabled: true,
        };
      }
      const quote = action.upgradeQuote;
      const isQuoteSimulated = quote?.simulated ?? isSimulatedMode;
      const targetName = action.targetPackage ? planName(action.targetPackage.id) : "the new plan";
      return {
        title: "Confirm upgrade",
        intro: `Move from ${currentName} to ${targetName} now: the full price is charged today, `
          + "all credits arrive at once and your month restarts from activation.",
        rows: quote
          ? [
            {label: "Charged today", value: amountCents(quote.amountCents, quote.simulated)},
            {label: "Credits added now", value: formatCreditCount(quote.credits)},
            {label: "New period", value: `${formatUtc(quote.periodStart)} - ${formatUtc(quote.periodEnd)}`},
            {
              label: `Estimated refund of unused ${currentName} credits`,
              value: `${formatCreditCount(quote.estimatedRefundCredits)} - ${amountCents(quote.estimatedRefundCents, quote.simulated)}, recorded separately`,
            },
          ]
          : [],
        note: isQuoteSimulated
          ? SIMULATED_NOTE
          : quote ? `${POLAR_CHECKOUT_NOTE} ${POLAR_REFUND_NOTE}` : POLAR_CHECKOUT_NOTE,
        confirmLabel: "Confirm upgrade",
        dismissLabel: "Not now",
        isConfirmDisabled: quote === null,
      };
    }
    case "downgrade": {
      if (action.phase === "failed") {
        return {
          title: "Downgrade failed",
          intro: null,
          rows: [],
          note: modeNote,
          confirmLabel: "Confirm downgrade",
          dismissLabel: "Keep current plan",
          isConfirmDisabled: true,
        };
      }
      const target = action.targetPackage;
      // Before submission this names the card that was clicked, never a plan an earlier downgrade scheduled; once
      // scheduled, it names the next plan the server returned.
      const targetName = target ? planName(target.id) : "the selected plan";
      const scheduledPlanId = change?.subscription.nextPlanId;
      const scheduledName = scheduledPlanId ? planName(scheduledPlanId) : targetName;
      if (done) {
        return {
          title: "Downgrade scheduled",
          intro: currentSubscription
            ? `${scheduledName} starts when the current period ends; nothing changes before then.`
            : "Your plan status was refreshed.",
          rows: currentSubscription
            ? [
              {label: "Current plan until", value: formatUtc(currentSubscription.periodEnd)},
              {label: "Next period", value: scheduledName},
            ]
            : [],
          note: modeNote,
          confirmLabel: "Confirm downgrade",
          dismissLabel: "Keep current plan",
          isConfirmDisabled: true,
        };
      }
      return {
        title: "Confirm downgrade",
        intro: subscription
          ? `Keep ${planName(subscription.planId)} and its credits until the period ends. From the next period, ${targetName} renews instead.`
          : null,
        rows: [
          ...(subscription ? [{label: "Current plan until", value: formatUtc(subscription.periodEnd)}] : []),
          ...(target && target.priceUsd !== null && target.credits !== null
            ? [{
              label: "Next period",
              value: `${targetName} - ${formatCreditCount(target.credits)} for ${formatUsdCents(target.priceUsd * 100)} / month`,
            }]
            : []),
        ],
        note: modeNote,
        confirmLabel: "Confirm downgrade",
        dismissLabel: "Keep current plan",
        isConfirmDisabled: subscription === null,
      };
    }
    case "resume": {
      if (done) {
        return {
          title: "Subscription resumed",
          intro: currentSubscription
            ? `${planName(currentSubscription.planId)} keeps renewing.`
            : "Your plan status was refreshed.",
          rows: currentSubscription ? [{label: "Renews", value: formatUtc(currentSubscription.periodEnd)}] : [],
          note: modeNote,
          confirmLabel: "Confirm resume",
          dismissLabel: "Not now",
          isConfirmDisabled: true,
        };
      }
      return {
        title: "Resume subscription",
        intro: subscription
          ? `Keep ${planName(subscription.planId)} renewing. The scheduled cancellation is removed.`
          : null,
        rows: subscription
          ? [
            {label: "Renews", value: formatUtc(subscription.periodEnd)},
            {
              label: "Renewal",
              value: `${formatUsdCents(subscription.priceCents)} / month for ${formatCreditCount(subscription.credits)}`,
            },
          ]
          : [],
        note: modeNote,
        confirmLabel: "Confirm resume",
        dismissLabel: "Not now",
        isConfirmDisabled: subscription === null,
      };
    }
    case "refund": {
      const purchase = action.refundPurchase;
      const purchaseName = purchase ? planName(purchase.packageId) : "this";
      if (action.phase === "processing" || action.phase === "failed") {
        return {
          title: action.phase === "processing" ? "Refund processing" : "Refund failed",
          intro: null,
          rows: [],
          note: modeNote,
          confirmLabel: "Confirm refund",
          dismissLabel: "Keep credits",
          isConfirmDisabled: true,
        };
      }
      if (done) {
        const refunded = result && !isChangeResponse(result) ? result : null;
        return {
          title: "Refund recorded",
          intro: refunded
            ? "The unused credits left your balance and the refund is on record."
            : "Your balance was refreshed.",
          rows: refunded
            ? [
              {label: "Refunded", value: refundLabel(refunded.refund)},
              ...(currentBalance
                ? [{label: "Balance", value: formatCreditCount(currentBalance.balance)}]
                : []),
            ]
            : [],
          note: modeNote,
          confirmLabel: "Confirm refund",
          dismissLabel: "Keep credits",
          isConfirmDisabled: true,
        };
      }
      const quote = action.refundQuote;
      const isRefundSimulated = quote?.simulated ?? purchase?.simulated ?? isSimulatedMode;
      return {
        title: "Confirm refund",
        intro: `Refund the unused credits of this ${purchaseName} purchase. Spent credits are not refundable.`,
        rows: quote && purchase
          ? [
            {label: "Unused credits", value: formatCreditCount(quote.credits)},
            {label: "Refund", value: amountCents(quote.amountCents, quote.simulated)},
            {
              label: "Original purchase",
              value: `${formatCreditCount(purchase.credits)} for ${formatUsdCents(purchase.amountCents ?? Math.round(purchase.priceUsd * 100))}`,
            },
          ]
          : [],
        note: isRefundSimulated ? SIMULATED_NOTE : quote && purchase ? POLAR_REFUND_NOTE : null,
        confirmLabel: "Confirm refund",
        dismissLabel: "Keep credits",
        isConfirmDisabled: quote === null,
      };
    }
    case "cancel":
      return {
        title: "Cancel subscription",
        intro: null,
        rows: [],
        note: modeNote,
        confirmLabel: "Confirm cancellation",
        dismissLabel: "Keep subscription",
        isConfirmDisabled: true,
      };
  }
};

const describeCheckout = (
  flow: CheckoutFlow,
  currentBalance: ICreditsBalance | null,
  planName: (planId: string) => string,
): {title: string; intro: string; phase: BillingActionPhase; rows: BillingSummaryRow[]} => {
  switch (flow.phase) {
    case "done":
      return {
        title: "Credits added",
        intro: flow.action === "upgrade"
          ? `${planName(flow.packageId)} is active and its credits are in your balance.`
          : "The credits are in your balance.",
        phase: "done",
        rows: currentBalance ? [{label: "Balance", value: formatCreditCount(currentBalance.balance)}] : [],
      };
    case "timeout":
      return {title: "Payment received", intro: CHECKOUT_TIMEOUT_MESSAGE, phase: "processing", rows: []};
    case "failed":
      return {title: "Payment failed", intro: CHECKOUT_FAILED_MESSAGE, phase: "failed", rows: []};
    case "embed":
    case "processing":
      return {title: "Payment received", intro: CHECKOUT_PROCESSING_MESSAGE, phase: "processing", rows: []};
  }
};

interface PricingPanelProps {
  onClose: () => void;
  isPricingSettled: boolean;
  checkoutFlow: CheckoutFlow | null;
  onOpenCheckout: (request: CheckoutRequest) => void;
  onDismissCheckout: () => void;
}

const PricingPanel: FC<PricingPanelProps> = ({
  onClose,
  isPricingSettled,
  checkoutFlow,
  onOpenCheckout,
  onDismissCheckout,
}) => {
  const toast = useToast();
  const workspaceId = useUserStore((state) => state.user?.workspaceId);
  const catalog = useCreditsStore((state) => state.catalog);
  const balance = useCreditsStore((state) => state.balance);
  const status = useCreditsStore((state) => state.status);
  const boundWorkspaceId = useCreditsStore((state) => state.workspaceId);
  const generation = useCreditsStore((state) => state.generation);
  const fetchBalance = useCreditsStore((state) => state.fetchBalance);
  const fetchUpgradeQuote = useCreditsStore((state) => state.fetchUpgradeQuote);
  const fetchRefundQuote = useCreditsStore((state) => state.fetchRefundQuote);
  const purchasePackage = useCreditsStore((state) => state.purchasePackage);
  const upgradeSubscription = useCreditsStore((state) => state.upgradeSubscription);
  const changeNextPlan = useCreditsStore((state) => state.changeNextPlan);
  const cancelSubscription = useCreditsStore((state) => state.cancelSubscription);
  const resumeSubscription = useCreditsStore((state) => state.resumeSubscription);
  const requestRefund = useCreditsStore((state) => state.requestRefund);
  const pricingView = useCreditsStore((state) => state.pricingView);
  const setPricingView = useCreditsStore((state) => state.setPricingView);
  const titleId = useId();
  const packagesTitleId = useId();
  const bodyRef = useRef<HTMLDivElement>(null);
  const viewTabs = useRef<Record<PricingView, HTMLElement | null>>({plans: null, billing: null});
  const [pendingPurchase, setPendingPurchase] = useState<ICreditPackage | null>(null);
  const [purchasedIds, setPurchasedIds] = useState<string[]>([]);
  const [purchases, setPurchases] = useState<ICreditPurchase[] | null>(null);
  const [isHistoryUnavailable, setIsHistoryUnavailable] = useState(false);
  const [usage, setUsage] = useState<ICreditUsageRow[] | null>(null);
  const [hasMoreUsage, setHasMoreUsage] = useState(false);
  const [isUsageUnavailable, setIsUsageUnavailable] = useState(false);
  const [action, setAction] = useState<ActiveAction | null>(null);
  // The dismissed action keeps rendering through the leave transition.
  const [shownAction, setShownAction] = useState<ActiveAction | null>(null);
  if (action !== null && action !== shownAction) setShownAction(action);
  const actionToken = useRef(0);
  const historyLoad = useRef(0);
  const usageLoad = useRef(0);
  // Usage is read lazily, once per mount, the first time the Billing view is shown; a settled checkout or a
  // workspace switch reads it again only when it was already requested.
  const usageRequested = useRef(false);

  // Everything below is bound to the store's workspace + request generation (C7): a workspace switch or a
  // logout drops the open confirmation, the Credits-added marks and the loaded purchase history.
  const fence = `${boundWorkspaceId ?? ""}:${generation}`;
  const previousFence = useRef(fence);
  useEffect(() => {
    if (previousFence.current === fence) return;
    previousFence.current = fence;
    actionToken.current += 1;
    historyLoad.current += 1;
    usageLoad.current += 1;
    usageRequested.current = false;
    setPendingPurchase(null);
    setPurchasedIds([]);
    setPurchases(null);
    setIsHistoryUnavailable(false);
    setUsage(null);
    setHasMoreUsage(false);
    setIsUsageUnavailable(false);
    setAction(null);
  }, [fence]);

  const loadPurchases = useCallback(async (): Promise<void> => {
    if (!workspaceId) return;
    historyLoad.current += 1;
    const load = historyLoad.current;
    const startedAt = requestFence();
    const isLatest = (): boolean => historyLoad.current === load && requestFence() === startedAt;
    try {
      const rows = await getCreditPurchases(workspaceId);
      if (!isLatest()) return;
      setPurchases(rows);
      setIsHistoryUnavailable(false);
    } catch {
      if (isLatest()) setIsHistoryUnavailable(true);
    }
  }, [workspaceId]);

  const loadUsage = useCallback(async (): Promise<void> => {
    if (!workspaceId) return;
    usageLoad.current += 1;
    const load = usageLoad.current;
    const startedAt = requestFence();
    const isLatest = (): boolean => usageLoad.current === load && requestFence() === startedAt;
    try {
      const result = await getCreditUsage(workspaceId);
      if (!isLatest()) return;
      setUsage(result.usage);
      setHasMoreUsage(result.hasMore);
      setIsUsageUnavailable(false);
    } catch {
      if (isLatest()) setIsUsageUnavailable(true);
    }
  }, [workspaceId]);

  const schemaVersion = balance?.schemaVersion;
  useEffect(() => {
    if (schemaVersion === 2) void loadPurchases();
  }, [schemaVersion, loadPurchases]);

  // The Plans view never pays for the usage read; the Billing view requests it once per mount and workspace.
  useEffect(() => {
    if (schemaVersion !== 2 || pricingView !== "billing" || usageRequested.current) return;
    usageRequested.current = true;
    void loadUsage();
  }, [schemaVersion, pricingView, loadUsage]);

  // A settled checkout re-reads the history so its row leaves Processing. The store already refreshed the balance
  // for a fulfilled or failed purchase; a timed-out wait did not.
  const checkoutPhase = checkoutFlow?.phase;
  useEffect(() => {
    if (checkoutPhase !== "done" && checkoutPhase !== "failed" && checkoutPhase !== "timeout") return;
    void loadPurchases();
    if (usageRequested.current) void loadUsage();
    if (checkoutPhase === "timeout" && workspaceId) void fetchBalance(workspaceId);
  }, [checkoutPhase, loadPurchases, loadUsage, fetchBalance, workspaceId]);

  const actions = balance?.allowedActions ?? LEGACY_ACTIONS;
  const liveSubscription = liveSubscriptionOf(balance);
  const planName = (planId: string): string =>
    catalog?.packages.find((pkg) => pkg.id === planId)?.name ?? PLAN_NAMES[planId] ?? planId;

  const showToast = (title: string, toastStatus: "error" | "warning" | "info"): void => {
    toast({position: "bottom-left", title, status: toastStatus, duration: 5000, isClosable: true});
  };

  // Each view starts at its top, the way a step change resets its scroll owner (DESIGN.md).
  const selectView = (view: PricingView): void => {
    setPricingView(view);
    bodyRef.current?.scrollTo({top: 0});
  };

  // Manage billing and Change plan unmount with their view, so focus follows to the selected tab, not the page body.
  const showView = (view: PricingView): void => {
    selectView(view);
    viewTabs.current[view]?.focus();
  };

  const ctaStateFor = (pkg: ICreditPackage): PricingCtaState => {
    if (pkg.cta === "contact_us") return "contact_us";
    if (purchasedIds.includes(pkg.id)) return "added";
    if (!isMonthly(pkg)) return actions.buyStarted ? "get_started" : "none";
    if (!liveSubscription) return actions.subscribe ? "get_started" : "none";
    if (liveSubscription.planId === pkg.id) return "current";
    if (liveSubscription.nextPlanId === pkg.id) return "scheduled";
    if ((pkg.credits ?? 0) > liveSubscription.credits) return actions.upgrade ? "upgrade" : "none";
    return actions.downgrade ? "downgrade" : "none";
  };

  // One persisted change serializes Pro/Business purchases, upgrades, downgrades, cancels and resumes (C5);
  // a Started purchase runs on the account fence and neither blocks them nor is blocked by them.
  const isSubscriptionActionLocked = pendingPurchase !== null && isMonthly(pendingPurchase);
  const isLockedFor = (pkg: ICreditPackage): boolean =>
    isSubscriptionActionLocked && isMonthly(pkg) && pendingPurchase?.id !== pkg.id;

  // Polar mode: the store holds the open checkout this response created, and payment continues in the embed.
  const openCheckoutFor = (
    action: CheckoutRequest["action"],
    packageId: string,
    nextAction: ICreditNextAction | undefined,
  ): boolean => {
    const checkout = useCreditsStore.getState().checkout;
    if (nextAction?.type !== "polar_checkout" || checkout?.status !== "open") return false;
    if (checkout.purchaseId !== nextAction.purchaseId) return false;
    onOpenCheckout({action, packageId, purchaseId: checkout.purchaseId, url: checkout.url});
    return true;
  };

  const isCheckoutProcessing = (pkg: ICreditPackage): boolean =>
    checkoutFlow?.phase === "processing" && checkoutFlow.packageId === pkg.id;

  const handlePurchase = async (pkg: ICreditPackage): Promise<void> => {
    if (!workspaceId) return;
    const startedAt = requestFence();
    setPendingPurchase(pkg);
    try {
      const result = await purchasePackage(workspaceId, pkg.id);
      if (result === null || requestFence() !== startedAt) return;
      if (openCheckoutFor("purchase", pkg.id, result.nextAction)) return;
      if (result.purchase.status === "fulfilled") {
        setPurchasedIds((ids) => [...ids, pkg.id]);
      } else {
        showToast(PURCHASE_PROCESSING_MESSAGE, "info");
      }
      void loadPurchases();
      await fetchBalance(workspaceId);
    } catch (error) {
      if (requestFence() !== startedAt) return;
      if (isAmbiguousResult(error)) {
        // The purchase may have committed. The store kept its key, so Get started replays it without a second
        // charge; meanwhile re-read the current state instead of reporting a failure.
        showToast(PURCHASE_UNCONFIRMED_MESSAGE, "warning");
        void loadPurchases();
        void fetchBalance(workspaceId);
      } else {
        showToast(isPurchasesDisabledError(error) ? PURCHASES_DISABLED_MESSAGE : PURCHASE_FAILED_MESSAGE, "error");
      }
    } finally {
      if (requestFence() === startedAt) setPendingPurchase(null);
    }
  };

  const beginAction = (initial: ActiveAction): ActionTicket => {
    actionToken.current += 1;
    setAction(initial);
    return {kind: initial.kind, token: actionToken.current, startedAt: requestFence()};
  };

  const isActionLive = ({token, startedAt}: ActionTicket): boolean =>
    actionToken.current === token && requestFence() === startedAt;

  const patchAction = (ticket: ActionTicket, changes: Partial<ActiveAction>): void => {
    if (isActionLive(ticket)) setAction((current) => (current ? {...current, ...changes} : current));
  };

  const dismissAction = (): void => {
    actionToken.current += 1;
    setAction(null);
  };

  // targetPlanId: the plan an upgrade moves to or a downgrade schedules. The action completes only for that plan, and
  // an upgrade's checkout belongs to it.
  const runAction = async (
    ticket: ActionTicket,
    perform: () => Promise<BillingActionResult | null>,
    requote: (ticket: ActionTicket) => Promise<Partial<ActiveAction>>,
    targetPlanId: ICreditMonthlyPlanId | null = null,
  ): Promise<void> => {
    if (!workspaceId) return;
    patchAction(ticket, {phase: "pending", message: null});
    try {
      const result = await perform();
      if (!isActionLive(ticket)) return;
      if (
        result !== null
        && ticket.kind === "upgrade"
        && targetPlanId !== null
        && isChangeResponse(result)
        && openCheckoutFor("upgrade", targetPlanId, result.nextAction)
      ) {
        dismissAction();
        return;
      }
      if (result === null) await fetchBalance(workspaceId);
      if (!isActionLive(ticket)) return;
      void loadPurchases();
      const phase = result === null ? "failed" : resultPhase(ticket.kind, result, targetPlanId);
      patchAction(ticket, {phase, message: resultMessage(phase), result});
    } catch (error) {
      if (!isActionLive(ticket)) return;
      const message = billingErrorMessage(error);
      if (isAmbiguousResult(error)) {
        // The change may have committed before its response was lost. The store kept this action's key, so
        // confirming again replays the same request and shows the real outcome; meanwhile re-read the current state.
        patchAction(ticket, {phase: "unknown", message});
        void loadPurchases();
        void fetchBalance(workspaceId);
        return;
      }
      if (!isStaleVersionError(error)) {
        patchAction(ticket, {phase: "ready", message});
        return;
      }
      // 409 stale_version: the subscription moved on. Re-read it and re-quote so the retry carries the current
      // expectedVersion (a new body, hence a new idempotency key) instead of replaying the stale request.
      const fresh = await fetchBalance(workspaceId);
      if (!isActionLive(ticket)) return;
      const freshSubscription = liveSubscriptionOf(fresh);
      const changes = await requote(ticket);
      if (!isActionLive(ticket)) return;
      patchAction(ticket, {phase: "ready", message, subscription: freshSubscription, balanceSnapshot: fresh, ...changes});
    }
  };

  const openUpgrade = async (pkg: ICreditPackage): Promise<void> => {
    const planId = monthlyPlanIdOf(pkg);
    if (!workspaceId || planId === null) return;
    const ticket = beginAction(
      startAction("upgrade", {phase: "loading", subscription: liveSubscription, targetPackage: pkg}),
    );
    const quote = await fetchUpgradeQuote(workspaceId, planId);
    patchAction(ticket, {phase: "ready", upgradeQuote: quote, message: quote ? null : QUOTE_UNAVAILABLE_MESSAGE});
  };

  const openRefund = async (purchase: ICreditPurchase): Promise<void> => {
    if (!workspaceId) return;
    const ticket = beginAction(startAction("refund", {phase: "loading", refundPurchase: purchase}));
    const quote = await fetchRefundQuote(workspaceId, purchase.id, purchase.refundableCredits);
    patchAction(ticket, {phase: "ready", refundQuote: quote, message: quote ? null : QUOTE_UNAVAILABLE_MESSAGE});
  };

  const handleCardAction = (pkg: ICreditPackage, cardAction: PricingCardAction): void => {
    if (cardAction === "purchase") {
      void handlePurchase(pkg);
    } else if (cardAction === "upgrade") {
      void openUpgrade(pkg);
    } else {
      beginAction(startAction("downgrade", {subscription: liveSubscription, targetPackage: pkg}));
    }
  };

  const requoteRefund = (purchase: ICreditPurchase) => async (ticket: ActionTicket): Promise<Partial<ActiveAction>> => {
    if (!workspaceId) return {};
    const rows = await getCreditPurchases(workspaceId).catch(() => null);
    if (!isActionLive(ticket)) return {};
    const fresh = rows?.find((row) => row.id === purchase.id) ?? purchase;
    void loadPurchases();
    const quote = fresh.refundableCredits > 0
      ? await fetchRefundQuote(workspaceId, fresh.id, fresh.refundableCredits)
      : null;
    if (!isActionLive(ticket)) return {};
    return {refundPurchase: fresh, refundQuote: quote};
  };

  const confirmAction = (effective: ICreditCancelEffective | null = null): void => {
    // An unknown result stays confirmable: the retry replays the retained key instead of submitting a new request.
    if (!action || !workspaceId || (action.phase !== "ready" && action.phase !== "unknown")) return;
    const ticket: ActionTicket = {kind: action.kind, token: actionToken.current, startedAt: requestFence()};
    const version = action.subscription?.version;
    // Upgrades and downgrades go to the card that was clicked; a stale version re-quotes that same plan.
    const targetPlanId = monthlyPlanIdOf(action.targetPackage);
    switch (action.kind) {
      case "upgrade": {
        const quote = action.upgradeQuote;
        if (!quote || targetPlanId === null) return;
        void runAction(
          ticket,
          () => upgradeSubscription(workspaceId, targetPlanId, quote.expectedVersion),
          async () => ({upgradeQuote: workspaceId ? await fetchUpgradeQuote(workspaceId, targetPlanId) : null}),
          targetPlanId,
        );
        return;
      }
      case "downgrade":
        if (version === undefined || targetPlanId === null) return;
        void runAction(ticket, () => changeNextPlan(workspaceId, targetPlanId, version), async () => ({}), targetPlanId);
        return;
      case "resume":
        if (version === undefined) return;
        void runAction(ticket, () => resumeSubscription(workspaceId, version), async () => ({}));
        return;
      case "cancel":
        if (version === undefined || effective === null) return;
        void runAction(ticket, () => cancelSubscription(workspaceId, effective, version), async () => ({}));
        return;
      case "refund": {
        const purchase = action.refundPurchase;
        const quote = action.refundQuote;
        if (!purchase || !quote) return;
        void runAction(
          ticket,
          () => requestRefund(workspaceId, purchase.id, quote.credits, quote.expectedVersion),
          requoteRefund(purchase),
        );
      }
    }
  };

  const shown = shownAction;
  const isSimulatedMode = catalog?.purchaseMode !== "polar";
  const confirmationView = shown && shown.kind !== "cancel"
    ? describeAction(shown, balance, planName, isSimulatedMode)
    : null;
  // The result reopens this dialog; its nested dialog opens only once the pricing dialog has finished entering, so the
  // pricing dialog adopts it as a child instead of hiding it from assistive tech and pointer input.
  const isCheckoutResultOpen = isPricingSettled && checkoutFlow !== null && checkoutFlow.phase !== "embed";
  // The dismissed result keeps rendering through the leave transition.
  const [shownCheckout, setShownCheckout] = useState<CheckoutFlow | null>(null);
  if (isCheckoutResultOpen && checkoutFlow !== shownCheckout) {
    setShownCheckout(checkoutFlow);
    // A paid Started or first-plan checkout marks its card Credits added, like a simulated purchase.
    if (checkoutFlow.phase === "done" && checkoutFlow.action === "purchase" && !purchasedIds.includes(checkoutFlow.packageId)) {
      setPurchasedIds((ids) => [...ids, checkoutFlow.packageId]);
    }
  }
  const checkoutView = shownCheckout ? describeCheckout(shownCheckout, balance, planName) : null;

  return (
    <>
      <TabGroup
        className="flex min-h-0 flex-1 flex-col"
        selectedIndex={PRICING_VIEWS.indexOf(pricingView)}
        onChange={(index) => selectView(PRICING_VIEWS[index])}
      >
        <div className="flex shrink-0 items-center gap-6 border-b border-n-300 px-6 py-4">
          <DialogTitle as="h2" id={titleId} className="h6 text-balance text-n-700">Plan & billing</DialogTitle>
          <TabList aria-labelledby={titleId} className="relative grid shrink-0 grid-cols-2 rounded-full bg-n-200 p-1">
            <span
              aria-hidden="true"
              className={`absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-brandPurple-500 transition-transform duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none ${pricingView === "billing" ? "translate-x-full" : "translate-x-0"}`}
            />
            <Tab ref={(node: HTMLElement | null) => { viewTabs.current.plans = node; }} className={VIEW_TAB_CLASS}>
              Plans
            </Tab>
            <Tab ref={(node: HTMLElement | null) => { viewTabs.current.billing = node; }} className={VIEW_TAB_CLASS}>
              Billing
            </Tab>
          </TabList>
          <button
            type="button"
            aria-label="Close dialog"
            className="ml-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-n-200 fill-n-700 text-0 transition-[background-color,transform] duration-200 active:scale-[0.96] motion-reduce:transition-colors hover:bg-n-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brandPurple-400"
            onClick={onClose}
          >
            <SmartIcon className="fill-inherit transition-colors" name="close" size={20}/>
          </button>
        </div>
        <TabPanels ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-6 pt-5">
          <TabPanel className={VIEW_PANEL_CLASS}>
            <MemberNotice/>
            <PlanSummaryLine onManageBilling={() => showView("billing")}/>
            <Box as="section" aria-labelledby={packagesTitleId} mt={6}>
              <Text id={packagesTitleId} className="text-balance" fontSize="sm" fontWeight="semibold" color="n.500">
                Choose a package for your workspace
              </Text>
              {catalog ? (
                <SimpleGrid columns={{base: 1, md: 2, xl: catalog.packages.length}} spacing={4} mt={3}>
                  {catalog.packages.map((pkg) => (
                    <PricingPackageCard
                      key={pkg.id}
                      pkg={pkg}
                      bullets={catalog.bullets}
                      ctaState={ctaStateFor(pkg)}
                      isPending={pendingPurchase?.id === pkg.id || isCheckoutProcessing(pkg)}
                      isLocked={isLockedFor(pkg)}
                      onAction={handleCardAction}
                    />
                  ))}
                </SimpleGrid>
              ) : (
                <Text mt={3} fontSize="sm" color="n.400">
                  {status === "loading" ? "Loading packages..." : "Packages are unavailable right now."}
                </Text>
              )}
            </Box>
          </TabPanel>
          <TabPanel className={VIEW_PANEL_CLASS}>
            <MemberNotice/>
            {balance?.schemaVersion === 2 ? (
              <>
                <SubscriptionPanel
                  isSubscriptionActionLocked={isSubscriptionActionLocked}
                  onChangePlan={() => showView("plans")}
                  onCancelSubscription={() => beginAction(
                    startAction("cancel", {subscription: liveSubscription, balanceSnapshot: balance}),
                  )}
                  onResumeSubscription={() => beginAction(startAction("resume", {subscription: liveSubscription}))}
                />
                <PurchaseHistory
                  purchases={purchases}
                  isUnavailable={isHistoryUnavailable}
                  allowedActions={actions}
                  packages={catalog?.packages}
                  onRefundPurchase={(purchase) => void openRefund(purchase)}
                  onRetry={() => void loadPurchases()}
                />
                <UsageHistory
                  rows={usage}
                  isUnavailable={isUsageUnavailable}
                  hasMore={hasMoreUsage}
                  showMigrationNote={Boolean(balance?.migratedFromMinutes)}
                  onRetry={() => void loadUsage()}
                />
              </>
            ) : (
              <Text fontSize="sm" color="n.400">
                {status === "loading" ? "Loading billing details..." : "Billing details are unavailable right now."}
              </Text>
            )}
          </TabPanel>
        </TabPanels>
      </TabGroup>
      {shown && confirmationView && (
        <BillingActionConfirmation
          isOpen={action !== null && action.kind !== "cancel"}
          title={confirmationView.title}
          intro={confirmationView.intro}
          rows={confirmationView.rows}
          note={confirmationView.note}
          phase={shown.phase}
          message={shown.message}
          confirmLabel={confirmationView.confirmLabel}
          dismissLabel={confirmationView.dismissLabel}
          isConfirmDisabled={confirmationView.isConfirmDisabled}
          onConfirm={() => confirmAction()}
          onDismiss={dismissAction}
        />
      )}
      {checkoutView && (
        <BillingActionConfirmation
          isOpen={isCheckoutResultOpen}
          title={checkoutView.title}
          intro={checkoutView.intro}
          rows={checkoutView.rows}
          note={null}
          phase={checkoutView.phase}
          message={null}
          confirmLabel="Done"
          dismissLabel="Close"
          onConfirm={onDismissCheckout}
          onDismiss={onDismissCheckout}
        />
      )}
      {shown?.kind === "cancel" && (
        <CancelSubscriptionDialog
          isOpen={action?.kind === "cancel"}
          subscriptionSnapshot={shown.subscription}
          planName={shown.subscription ? planName(shown.subscription.planId) : "Your plan"}
          planRemainingCredits={shown.balanceSnapshot?.planRemainingCredits ?? 0}
          nonPlanCredits={shown.balanceSnapshot?.nonPlanCredits ?? 0}
          phase={shown.phase}
          message={shown.message}
          result={shown.result && isChangeResponse(shown.result) ? shown.result : null}
          currentBalance={balance}
          isSimulated={isSimulatedMode}
          onConfirm={(effective) => confirmAction(effective)}
          onDismiss={dismissAction}
        />
      )}
    </>
  );
};

const PricingModal: FC = () => {
  const isPricingOpen = useCreditsStore((state) => state.isPricingOpen);
  const closePricing = useCreditsStore((state) => state.closePricing);
  const catalog = useCreditsStore((state) => state.catalog);
  const fetchCatalog = useCreditsStore((state) => state.fetchCatalog);
  const awaitCheckoutResult = useCreditsStore((state) => state.awaitCheckoutResult);
  const followCheckoutResult = useCreditsStore((state) => state.followCheckoutResult);
  const cancelCheckoutResult = useCreditsStore((state) => state.cancelCheckoutResult);
  const toast = useToast();
  const [checkoutFlow, setCheckoutFlow] = useState<CheckoutFlow | null>(null);
  const [isPricingSettled, setIsPricingSettled] = useState(false);
  const embedRef = useRef<PolarEmbedCheckout | null>(null);
  // Removes what a still-loading embed attempt already put on the page; the SDK only returns the instance on "loaded".
  const disposePendingEmbedRef = useRef<(() => void) | null>(null);
  const checkoutToken = useRef(0);

  useEffect(() => {
    if (isPricingOpen && catalog === null) void fetchCatalog();
  }, [isPricingOpen, catalog, fetchCatalog]);

  useEffect(() => () => {
    checkoutToken.current += 1;
    disposePendingEmbedRef.current?.();
    disposePendingEmbedRef.current = null;
    embedRef.current?.close();
    embedRef.current = null;
    cancelCheckoutResult();
  }, [cancelCheckoutResult]);

  const settleCheckout = async (token: number, purchaseId: string): Promise<void> => {
    await awaitCheckoutResult(purchaseId);
    if (checkoutToken.current !== token) return;
    const checkout = useCreditsStore.getState().checkout;
    const status = checkout?.purchaseId === purchaseId ? checkout.status : null;
    const phase: CheckoutPhase | null = status === "fulfilled"
      ? "done"
      : status === "failed" ? "failed" : status === "timed_out" ? "timeout" : null;
    // Cancelled or superseded (a workspace switch or logout): the result belongs to nobody on screen.
    setCheckoutFlow((current) => (current && phase ? {...current, phase} : null));
    if (phase === "timeout") void followLateGrant(token, purchaseId);
  };

  // A timed-out payment may still be granted late; only the server's fulfilled status turns it into Credits added.
  const followLateGrant = async (token: number, purchaseId: string): Promise<void> => {
    const purchase = await followCheckoutResult(purchaseId);
    if (checkoutToken.current !== token || purchase === null) return;
    const phase: CheckoutPhase = purchase.status === "fulfilled" ? "done" : "failed";
    setCheckoutFlow((current) => (current?.purchaseId === purchaseId ? {...current, phase} : current));
  };

  const openCheckout = async (request: CheckoutRequest): Promise<void> => {
    checkoutToken.current += 1;
    const token = checkoutToken.current;
    const isLive = (): boolean => checkoutToken.current === token;
    disposePendingEmbedRef.current?.();
    disposePendingEmbedRef.current = null;
    embedRef.current?.close();
    embedRef.current = null;
    setCheckoutFlow({action: request.action, packageId: request.packageId, purchaseId: request.purchaseId, phase: "embed"});
    // PolarEmbedCheckout.create synchronously appends its loader, iframe and style and locks body scroll, but
    // resolves only once the iframe reports "loaded"; diffing the page around the call finds this attempt's nodes.
    const existing = new Set<Element>([...document.body.children, ...document.head.children]);
    let isDisposed = false;
    const creating = PolarEmbedCheckout.create(request.url, {
      theme: "light",
      // The SDK's default "loaded" handling removes its loader from the body; once disposed that node is gone.
      onLoaded: (event) => {
        if (isDisposed) event.preventDefault();
      },
    });
    const inserted = [...document.body.children, ...document.head.children].filter((node) => !existing.has(node));
    const disposePending = (): void => {
      isDisposed = true;
      for (const node of inserted) node.remove();
      document.body.classList.remove("polar-no-scroll");
    };
    disposePendingEmbedRef.current = disposePending;
    const releasePending = (): void => {
      if (disposePendingEmbedRef.current === disposePending) disposePendingEmbedRef.current = null;
    };
    let embed: PolarEmbedCheckout;
    try {
      embed = await creating;
    } catch {
      releasePending();
      if (!isLive()) return;
      disposePending();
      // The store kept the purchase key, so the next Get started replays the same checkout session.
      setCheckoutFlow(null);
      toast({position: "bottom-left", title: CHECKOUT_OPEN_FAILED_MESSAGE, status: "error", duration: 5000, isClosable: true});
      return;
    }
    releasePending();
    if (!isLive()) {
      embed.close();
      return;
    }
    embedRef.current = embed;
    // After "confirmed" Polar locks closing while the payment processes; a close request then changes nothing.
    let isConfirmed = false;
    embed.addEventListener("confirmed", () => {
      isConfirmed = true;
    });
    embed.addEventListener("close", () => {
      if (!isLive() || isConfirmed) return;
      embedRef.current = null;
      setCheckoutFlow(null);
    });
    embed.addEventListener("success", (event) => {
      // No redirect: the result is shown here, and credits arrive only from the verified order webhook.
      event.preventDefault();
      if (!isLive()) return;
      embed.close();
      embedRef.current = null;
      setCheckoutFlow((current) => (current ? {...current, phase: "processing"} : current));
      void settleCheckout(token, request.purchaseId);
    });
  };

  const dismissCheckout = (): void => {
    // Only a timed-out checkout has a follow-up to stop; an earlier wait still settles the purchase key.
    if (checkoutFlow?.phase === "timeout") cancelCheckoutResult();
    checkoutToken.current += 1;
    setCheckoutFlow(null);
  };

  const handleClose = (): void => {
    dismissCheckout();
    closePricing();
  };

  return (
    <Transition show={isPricingOpen && checkoutFlow?.phase !== "embed"} as={Fragment}>
      <Dialog className="fixed inset-0 z-[60] flex p-6 md:px-4" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-n-700/75" aria-hidden="true"/>
        </TransitionChild>
        {/* A fixed, viewport-bounded height: switching views never moves the header, and only the body scrolls.
            46rem holds the Plans view (member notice included) without scrolling at 1280x900, and up to 80rem of
            width keeps all five catalog cards in one row. */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
          afterEnter={() => setIsPricingSettled(true)}
          beforeLeave={() => setIsPricingSettled(false)}
        >
          <DialogPanel
            className="relative z-10 m-auto flex h-[min(calc(100dvh-3rem),46rem)] w-full max-w-[80rem] flex-col overflow-hidden rounded-3xl bg-white text-n-700"
          >
            <PricingPanel
              onClose={handleClose}
              isPricingSettled={isPricingSettled}
              checkoutFlow={checkoutFlow}
              onOpenCheckout={(request) => void openCheckout(request)}
              onDismissCheckout={dismissCheckout}
            />
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default PricingModal;
